import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { VideoUploadService, UploadProgress } from '@/pages/Beta/services/videoUploadService';
import { errorReporter } from '@/services/errorReportingService';

interface UseVideoUploadOptions {
  onUploadComplete?: (result: { success: boolean; noteId?: string; filePath?: string; error?: string }) => void;
  redirectOnSuccess?: boolean;
  redirectDelay?: number;
  navigateState?: Record<string, unknown>;
}

interface UseVideoUploadReturn {
  isUploading: boolean;
  uploadProgress: UploadProgress | null;
  uploadError: string | null;
  uploadVideo: (file: File, durationSeconds?: number) => Promise<void>;
  clearUploadError: () => void;
}

export function useVideoUpload(options: UseVideoUploadOptions = {}): UseVideoUploadReturn {
  const { onUploadComplete, redirectOnSuccess = true, redirectDelay = 1500, navigateState } = options;

  const navigate = useNavigate();
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref-based guard: state updates are async, so a double-click in the same tick
  // would pass an isUploading check and create two notes
  const uploadInFlightRef = useRef(false);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  const uploadVideo = useCallback(
    async (file: File, durationSeconds?: number): Promise<void> => {
      if (uploadInFlightRef.current) return;
      uploadInFlightRef.current = true;

      setIsUploading(true);
      setUploadError(null);
      setUploadProgress(null);

      try {
        const uploadService = new VideoUploadService();

        const result = await uploadService.uploadVideo(
          file,
          (progress) => {
            setUploadProgress(progress);
          },
          durationSeconds,
        );

        onUploadComplete?.(result);

        if (result.success && result.noteId) {
          if (redirectOnSuccess) {
            redirectTimerRef.current = setTimeout(() => {
              navigate(`/note/${result.noteId}`, navigateState ? { state: navigateState } : undefined);
            }, redirectDelay);
          }
        } else if (result.error) {
          setUploadError(result.error);
        }
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Upload failed';
        setUploadError(errorMessage);
        errorReporter.critical('Video upload hook caught unhandled error', error, {
          feature: 'upload',
          operation: 'uploadVideo',
        });
      } finally {
        uploadInFlightRef.current = false;
        setIsUploading(false);
        setUploadProgress(null);
      }
    },
    [navigate, onUploadComplete, redirectOnSuccess, redirectDelay, navigateState],
  );

  const clearUploadError = useCallback(() => {
    setUploadError(null);
  }, []);

  return {
    isUploading,
    uploadProgress,
    uploadError,
    uploadVideo,
    clearUploadError,
  };
}
